import React from "react";

const VARIANTS = {
  default: "border-line-strong bg-surface-3 text-mid",
  success: "border-emerald-500/30 bg-emerald-500/[.12] text-emerald-300",
  danger: "border-rose-500/30 bg-rose-500/[.12] text-rose-300",
  warning: "border-amber-500/30 bg-amber-500/[.12] text-amber-300",
  info: "border-blue-500/30 bg-blue-500/[.12] text-blue-300",
  cyan: "border-cyan-500/30 bg-cyan-500/[.12] text-cyan-300",
  purple: "border-violet-500/30 bg-violet-500/[.12] text-violet-300",
  orange: "border-orange-500/30 bg-orange-500/[.12] text-orange-300",
  live: "border-rose-500/45 bg-rose-600 text-white",
  paper: "border-amber-500/35 bg-amber-500/[.14] text-amber-300",
  glow: "border-emerald-500/35 bg-emerald-500/[.12] text-emerald-300 shadow-[0_0_20px_-6px_rgb(var(--c-emerald-500)/.5)]",
};

const SIZES = {
  xs: "h-5 gap-1 px-1.5 text-[10px]",
  sm: "h-6 gap-1.5 px-2 text-2xs",
  md: "h-7 gap-1.5 px-2.5 text-xs",
};

export function Badge({ children, variant = "default", size = "sm", pulse = false, className = "" }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border font-semibold tracking-wide
        ${VARIANTS[variant] || VARIANTS.default} ${SIZES[size] || SIZES.sm} ${className}`}
    >
      {pulse && (
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      )}
      {children}
    </span>
  );
}

const DOT_TONES = {
  online: "bg-emerald-400 shadow-[0_0_10px_rgb(var(--c-emerald-400)/.85)]",
  offline: "bg-rose-400",
  warning: "bg-amber-400",
  pending: "bg-blue-400",
};

const DOT_SIZES = { sm: "h-1.5 w-1.5", md: "h-2 w-2", lg: "h-2.5 w-2.5" };

export function StatusDot({ status = "offline", size = "md", className = "" }) {
  return (
    <span className={`inline-block shrink-0 rounded-full ${DOT_TONES[status] || DOT_TONES.offline} ${DOT_SIZES[size]} ${className}`} />
  );
}

export function SignalBadge({ signal, confidence }) {
  const variants = { BUY: "success", SELL: "danger", HOLD: "warning" };
  return (
    <div className="flex items-center gap-2">
      <Badge variant={variants[signal] || "default"}>{signal}</Badge>
      {confidence !== undefined && (
        <span className="text-2xs font-semibold tabular-nums text-low">{(confidence * 100).toFixed(0)}%</span>
      )}
    </div>
  );
}

/** PAPER vs LIVE is the single most consequential bit of state in the app.
 *  `halted` (kill switch engaged) rides on the same pill so it is visible at
 *  every width, next to the mode it applies to. */
export function TradingModeBadge({ isPaper, halted = false, size = "sm", className = "" }) {
  return (
    <Badge
      variant={isPaper ? "paper" : "live"}
      size={size}
      pulse={!isPaper && !halted}
      className={`uppercase tracking-label ${!isPaper ? "font-black shadow-[0_0_14px_-2px_rgb(var(--c-rose-500)/.7)]" : ""} ${className}`}
    >
      {isPaper ? "Paper" : "Live"}
      {halted && <span className="ml-1 rounded bg-slate-950/50 px-1 text-[9px] font-black text-rose-200">HALTED</span>}
    </Badge>
  );
}
